import type { WebappConfig } from "@color-finder/config";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Link,
  Typography,
} from "@mui/material";
import { defaultSettings } from "providers/SettingsProvider";
import {
  createContext,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuthUser } from "../hooks/authentication";
import { useAuthenticatedRequest } from "../hooks/request";

const isQaEnvironment =
  window.location.hostname.startsWith("qa-") ||
  window.location.hostname === "localhost";

const defaultWebappConfig = {
  ux: {
    general: {
      inputDebounceTimeMs: 500,
    },
    theme: {
      color: {
        primary: "cyan",
        secondary: "magenta",
      },
    },
  },
  landing_page: {
    enabled: false,
    carousel: {
      enabled: false,
    },
    homepage_v2: {
      enabled: isQaEnvironment,
    },
  },
} as WebappConfig;

export interface GlobalRequestErrors {
  networkError: boolean;
  setNetworkError: Dispatch<boolean>;
}

export const GlobalRequestErrorsContext = createContext<GlobalRequestErrors>({
  networkError: false,
  setNetworkError: () => undefined,
});

export interface AppConfigContextValue {
  config: WebappConfig | undefined;
  setConfig: Dispatch<WebappConfig | undefined>;
  setExternalViewVisible: Dispatch<boolean>;
}

export const AppConfigContext = createContext<AppConfigContextValue>({
  config: undefined,
  setConfig: (_) => undefined,
  setExternalViewVisible: (_) => undefined,
});

export const useAppConfig = (): [
  config: WebappConfig,
  loading: boolean,
  setExternalViewVisible: Dispatch<boolean>,
] => {
  const { config, setExternalViewVisible } =
    useContext<AppConfigContextValue>(AppConfigContext);

  return [config!, config === undefined, setExternalViewVisible];
};

export const AppConfigProvider: FC<PropsWithChildren> = ({ children }) => {
  const [config, setConfig] = useState<WebappConfig>();
  const [networkError, setNetworkError] = useState(false);
  const externalViewVisibleFromStorage = JSON.parse(
    localStorage.getItem("search-settings") as string,
  )?.externalViewVisible;
  const [externalViewVisible, setExternalViewVisible] = useState<boolean>(
    externalViewVisibleFromStorage ?? defaultSettings.externalViewVisible,
  );

  const requestConfig = useMemo(() => {
    const requestParams: {
      params: { cache: number; isExternalView?: boolean };
    } = {
      params: {
        cache: Date.now(),
      },
    };

    if (externalViewVisible) {
      requestParams.params.isExternalView = externalViewVisible;
    }

    return requestParams;
  }, [externalViewVisible]);

  const [result, error, isLoading] = useAuthenticatedRequest<WebappConfig>(
    "/v0/config/webapp",
    requestConfig,
  );
  const { user } = useAuthUser();

  useEffect(() => {
    if (!isLoading) {
      setConfig(result ?? defaultWebappConfig);
    } else {
      setConfig(undefined);
    }
  }, [result, isLoading]);

  if (networkError) {
    return (
      <Box p={4}>
        <Alert severity={"info"}>
          <AlertTitle>No Network connection</AlertTitle>
          <Typography mb={2}>
            There seems to be an issue with your internet connection. Please
            check your device settings and reload the page.
          </Typography>
          <Link href={"/"}>
            <Button variant="outlined">Reload Page</Button>
          </Link>
        </Alert>
      </Box>
    );
  }

  if (user && error) {
    return (
      <Box p={4}>
        <Alert severity={"error"}>
          <AlertTitle>Could not launch application.</AlertTitle>
          <Typography>Failed to load application configuration.</Typography>
          <Typography>{error.message}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <GlobalRequestErrorsContext.Provider
      value={{ networkError, setNetworkError }}
    >
      <AppConfigContext.Provider
        value={{ config, setConfig, setExternalViewVisible }}
      >
        {children}
      </AppConfigContext.Provider>
    </GlobalRequestErrorsContext.Provider>
  );
};
